import React from 'react';
import { ReactComponent as SharlyLogo } from '../logo.svg';
import type { University, User } from '../types';
import { config } from '../config/env';

interface LoginPageProps {
  universities: University[];
  users: User[];
  loginMode: boolean;
  selectedUniversity: string;
  email: string;
  password: string;
  error: string;
  onLoginModeChange: (mode: boolean) => void;
  onSelectedUniversityChange: (id: string) => void;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onLogin: (universities: University[], users: User[]) => Promise<void>;
}

const LoginPage: React.FC<LoginPageProps> = ({
  universities,
  users,
  loginMode,
  selectedUniversity,
  email,
  password,
  error,
  onLoginModeChange,
  onSelectedUniversityChange,
  onEmailChange,
  onPasswordChange,
  onLogin,
}) => {
  const handleSubmit = () => {
    onLogin(universities, users);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
            <SharlyLogo className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">University Management</h1>
          <p className="text-gray-600">Sign in to manage users and universities</p>
        </div>

        <div className="space-y-4" onKeyDown={handleKeyDown}>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => onLoginModeChange(true)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                loginMode ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              University Admin
            </button>
            <button
              onClick={() => onLoginModeChange(false)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                !loginMode ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              System Admin
            </button>
          </div>

          {loginMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
              <select
                value={selectedUniversity}
                onChange={(e) => onSelectedUniversityChange(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select University</option>
                {universities.map(uni => (
                  <option key={uni.id} value={uni.id}>{uni.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder={loginMode ? 'university-admin@domain.edu' : config.admin.email}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder="Enter password"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
